/*
 * Merchandising signal for Home page blocks: view_item_list / select_item
 * for product carousels and lookbook hotspots, view_promotion /
 * select_promotion for hero/banner/collection-tile CTAs.
 *
 * Item identity mirrors babalu-ecommerce-analytics.js (item_id = barcode)
 * but omits item_size: at listing time no size has been selected yet, so
 * requiring it here would silently drop every impression. Size becomes
 * mandatory again once a real cart/PDP event fires.
 *
 * Impressions fire when a block enters the viewport (IntersectionObserver)
 * reporting whatever is currently rendered in the DOM at that moment, since
 * the theme's carousel engine has no public "which slide is visible" API to
 * hook into. Lookbook hotspots are the exception: their product card lives
 * inside a native <dialog>, hidden until opened, so their impression fires
 * on the dialog's own `toggle` event instead of on scroll visibility.
 */
(function () {
  'use strict';

  function readJSON(node) {
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return null;
    }
  }

  function push(eventName, payload) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push(Object.assign({ event: eventName }, payload));
  }

  function buildItem(node, index) {
    var data = readJSON(node);
    if (!data) return null;
    var variant = data.variant || {};
    var taxonomy = data.taxonomy || {};
    var item = {
      item_id: String(variant.barcode || ''),
      item_name: data.title || '',
      item_brand: taxonomy.brand || data.vendor || '',
      item_category: taxonomy.department || data.type || '',
      item_category2: taxonomy.category || '',
      item_category3: taxonomy.type || '',
      item_category4: taxonomy.product_type || '',
      item_collection: taxonomy.collection || '',
      item_product: taxonomy.product || '',
      item_line: taxonomy.line || '',
      item_variant: variant.title || '',
      price: Number(variant.price || 0),
      item_sku: variant.sku || '',
      item_barcode: String(variant.barcode || ''),
      shopify_variant_id: String(variant.id || ''),
      index: index || 1
    };
    return item.item_id && item.item_sku && item.item_barcode ? item : null;
  }

  // Reported item ids (barcode) per listId, not just a listId-level flag:
  // infinite-scroll pages (and predictive search's per-keystroke re-render,
  // though that gets a fresh listId per query so this rarely matters there)
  // add MORE cards under the SAME listId later. A plain "reported once"
  // flag would permanently block every page after the first from ever
  // being credited. Tracking which items already fired lets each new batch
  // of cards produce its own view_item_list carrying only what's new.
  var seenListItems = Object.create(null);

  // Above-the-fold lists (the default active carousel tab, banner-product
  // carousel, video-shoppable) can intersect the viewport before the
  // carousel library finishes hydrating real product cards into the DOM,
  // so the first read of their item-data JSON can be empty even though the
  // list itself is visible. Retry a few times before giving up rather than
  // reporting a list with no items. Also naturally covers "nothing new
  // since the last report" (retries, finds nothing again, gives up quietly).
  var LIST_IMPRESSION_RETRY_DELAYS_MS = [150, 300, 600];

  function collectNewListItems(listId) {
    var selector;
    try {
      selector = '[data-babalu-item-list-id="' + CSS.escape(listId) + '"] [data-babalu-item]';
    } catch (error) {
      return [];
    }
    var seen = seenListItems[listId] || (seenListItems[listId] = Object.create(null));
    var position = Object.keys(seen).length;
    var items = [];
    document.querySelectorAll(selector).forEach(function (node) {
      var item = buildItem(node, position + 1);
      if (item && !seen[item.item_id]) {
        seen[item.item_id] = true;
        position += 1;
        items.push(item);
      }
    });
    return items;
  }

  function pushListImpression(listId, listName, attempt) {
    var items = collectNewListItems(listId);
    if (!items.length) {
      if (attempt < LIST_IMPRESSION_RETRY_DELAYS_MS.length) {
        setTimeout(function () {
          pushListImpression(listId, listName, attempt + 1);
        }, LIST_IMPRESSION_RETRY_DELAYS_MS[attempt]);
      }
      return;
    }
    push('view_item_list', {
      item_list_id: listId,
      item_list_name: listName || listId,
      ecommerce: { items: items }
    });
  }

  function reportListImpression(listId, listName) {
    if (!listId) return;
    pushListImpression(listId, listName, 0);
  }

  function observeItemLists() {
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        reportListImpression(
          entry.target.getAttribute('data-babalu-item-list-id'),
          entry.target.getAttribute('data-babalu-item-list-name')
        );
      });
    }, { threshold: 0.25 });

    function observeCard(card) { observer.observe(card); }
    document.querySelectorAll('[data-babalu-item-list-id]').forEach(observeCard);

    // Infinite-scroll pagination (collections/search) appends later pages of
    // cards via AJAX after this script's initial scan — without this, only
    // page 1 is ever observed. The same mutation also covers the predictive
    // search dropdown, which replaces its whole results subtree via
    // innerHTML on every keystroke (see babalu-predictive-search list_id,
    // which already varies per query so each replacement is a fresh,
    // un-deduped impression rather than reusing a stale listId).
    new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          if (node.matches && node.matches('[data-babalu-item-list-id]')) observeCard(node);
          if (node.querySelectorAll) node.querySelectorAll('[data-babalu-item-list-id]').forEach(observeCard);
        });
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  function observeDialogRevealedLists() {
    document.querySelectorAll('dialog').forEach(function (dialog) {
      var card = dialog.querySelector('[data-babalu-item-list-id]');
      if (!card) return;
      dialog.addEventListener('toggle', function () {
        if (!dialog.open) return;
        reportListImpression(
          card.getAttribute('data-babalu-item-list-id'),
          card.getAttribute('data-babalu-item-list-name')
        );
      });
    });
  }

  var seenPromos = Object.create(null);

  function promoPayload(marker) {
    var payload = {
      promotion_id: marker.getAttribute('data-babalu-promo-id') || '',
      promotion_name: marker.getAttribute('data-babalu-promo-name') || '',
      creative_slot: marker.getAttribute('data-babalu-promo-slot') || ''
    };
    // Only banners shown on several pages (collection promo) carry a
    // location; Home blocks keep their original three-field payload.
    var location = marker.getAttribute('data-babalu-promo-location');
    if (location) payload.location_id = location;
    return payload;
  }

  function observePromotions() {
    // Blocks that report their own impression (the promo popup, shown on a
    // timer inside a <dialog>) are left out so the view is never doubled.
    var blocks = document.querySelectorAll('[data-babalu-promo-block]:not([data-babalu-promo-manual])');
    if (!blocks.length) return;
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        var marker = entry.target.querySelector('[data-babalu-promo]');
        if (!marker) return;
        var id = marker.getAttribute('data-babalu-promo-id');
        if (!id || seenPromos[id]) return;
        seenPromos[id] = true;
        push('view_promotion', promoPayload(marker));
      });
    }, { threshold: 0.25 });
    blocks.forEach(function (block) { observer.observe(block); });
  }

  document.addEventListener('click', function (event) {
    var anchor = event.target.closest && event.target.closest('a[href]');
    if (!anchor) return;

    var card = anchor.closest('[data-babalu-item-list-id]');
    if (card) {
      var itemNode = card.querySelector('[data-babalu-item]');
      var item = itemNode && buildItem(itemNode, 1);
      if (item) {
        push('select_item', {
          item_list_id: card.getAttribute('data-babalu-item-list-id'),
          item_list_name: card.getAttribute('data-babalu-item-list-name'),
          ecommerce: { items: [item] }
        });
      }
      return;
    }

    var promoBlock = anchor.closest('[data-babalu-promo-block]');
    // "Ver condiciones" is not a click on the promotion itself: reported
    // through the already-live select_content tag instead.
    if (promoBlock && anchor.hasAttribute('data-babalu-promo-terms')) {
      var termsMarker = promoBlock.querySelector('[data-babalu-promo]');
      if (termsMarker) {
        push('select_content', {
          content_type: 'promotion_terms',
          item_id: termsMarker.getAttribute('data-babalu-promo-id') || '',
          item_name: termsMarker.getAttribute('data-babalu-promo-name') || ''
        });
      }
      return;
    }
    if (promoBlock) {
      var marker = promoBlock.querySelector('[data-babalu-promo]');
      if (marker) push('select_promotion', promoPayload(marker));
    }
  }, true);

  // Always on, not gated on a list already being in the DOM at load: the
  // predictive search dropdown (present in the header on every page) only
  // gets its [data-babalu-item-list-id] cards after the shopper types a
  // query, so a page with no other product list (e.g. a blog post) would
  // otherwise never arm the observer that predictive search depends on.
  observeItemLists();
  if (document.querySelector('dialog')) {
    observeDialogRevealedLists();
  }
  if (document.querySelector('[data-babalu-promo-block]')) {
    observePromotions();
  }

  window.BabaluHomeImpressions = {
    buildItem: buildItem,
    promoPayload: promoPayload
  };
}());
